import AnimationController from './animation';
import HeightController from './height';

import { easing, getRootOffset, validateParamNumber } from '../helpers';

// setup instructions:

// 1. requires external calls to resizeUpdate() and resizeRender()
// 2. requires external calls to update() and render()

class ScrollController {

	attributes = {
		sticky: 'data-sticky',
		stickyOffset: 'data-sticky-offset'
	};

	classes = {
		isLocked: 'is-no-scroll'
	};

	coords = {
		top: 0, 			// equivalent of window.scrollY
		bottom: 0, 			// top + window.innerHeight
		eased: 0, 			// current eased position
		easedBottom: 0, 	// current eased position + window.innerHeight
		viewportHeight: 0, 	// viewport height
		height: 0, 			// total height of all elements

		sticky: {
			top: 0,
			offset: 0
		}
	};

	ease = {
		duration: 35,
		function: 'easeOutQuint',
		from: 0,
		frame: 0
	};

	flags = {
		isJumpMode: false,
		isLocked: false,
		isPageIn: false,
		isRendering: true,
		isSmoothScrolling: false
	};

	states = {
		chunk: 0, 			// size of last chunk (current eased position - previous eased position)
		direction: 0, 		// 0, 1, -1
		lockTop: 0,			// scroll top when lock occurs
		percent: 0, 		// percentage of window scrolled,
		lockY: 0
	};

	// elements

	container;

	include = [];

	children = [];

	stickies = [];

	// <==]--------------------------->

	initialize(container, include) {
		this.container = container;
		this.include = include; // array of strings to query

		this.disableSmoothScrolling(true);
	}

	// gather children

	gatherChildren() {
		this.children = [];
		const elements = [];
		for (const selector of this.include) {
			elements.push(...this.container.querySelectorAll(selector));
		}

		for (const el of elements) {
			this.children.push({
				el,
				adjustment: 0,
				height: 0,
				initial: false,
				offset: 0,
				space: 0,
				translate: 0,
				update: false
			});
		}
	}

	// gather stickies

	gatherStickies() {
		this.stickies = [];
		const stickies = document.querySelectorAll(`[${this.attributes.sticky}]`);
		for (const el of stickies) {
			this.stickies.push({
				el,
				adjustment: parseInt(el.dataset.adjustment) || 0,
				height: 0,
				offset: 0,
				space: 0,
				translate: 0,
				update: false
			});
		}
	}

	measureChildren() {
		for (const child of this.children) {
			child.offset = getRootOffset(child.el).top;
			child.height = child.el.clientHeight;
			if (child.isSticky) {
				child.space = child.el.parentNode.clientHeight;
			} else {
				child.space = 0;
			}
		}
	}

	measureStickies() {
		for (const sticky of this.stickies) {
			sticky.offset = getRootOffset(sticky.el).top - validateParamNumber(sticky.el, this.attributes.stickyOffset);
			sticky.height = sticky.el.clientHeight;
			sticky.space = sticky.el.parentNode.clientHeight;
		}
	}

	measureViewport() {
		this.coords.viewportHeight = window.innerHeight;
	}

	updateHeight() {
		if (this.flags.isSmoothScrolling && this.children.length) {
			const lastChild = this.children.length - 1;
			this.coords.height = this.children[lastChild].offset + this.children[lastChild].height;
			HeightController.adjust(this.coords.height);
		}
	}

	scrollTo(top, jump) {
		this.flags.isJumpMode = jump;
		window.scrollTo(false, top);
	}

	// page in and out disables scrolling and resets position to top for new page

	pageOut() {
		if (this.flags.isPageIn) {
			this.flags.isPageIn = false;
			this.updateHeight();
			this.lock();
		}
	}

	pageIn(scrollY = 0) {
		if (!this.flags.isPageIn) {
			this.flags.isPageIn = true;
			this.unlock();
			this.refresh(true);
			AnimationController.one({
				update: () => true,
				render: () => {
					this.scrollTo(scrollY, true);
				}
			});
		}
	}

	// lock and unlock scrolling

	lock() {
		if (!this.flags.isLocked) {
			this.flags.isLocked = true;
			this.states.lockY = this.coords.top;
			document.body.classList.add(this.classes.isLocked);
		}
	}

	unlock() {
		if (this.flags.isLocked) {
			this.flags.isLocked = false;

			document.body.classList.remove(this.classes.isLocked);

			this.coords.top = this.states.lockY;
			this.coords.bottom = this.coords.top + this.coords.viewportHeight;
			this.coords.eased = this.states.lockY;
			this.coords.easedBottom = this.coords.eased + this.coords.viewportHeight;
			this.ease.from = this.states.lockY;

			this.scrollTo(this.states.lockY, true);
			this.states.lockY = -1;
			this.render();
		}
	}

	//

	updateDirection() {
		const top = window.scrollY || document.documentElement.scrollTop || 0;
		if (top > this.coords.top) {
			this.states.direction = 1;
		} else if (top === this.coords.top) {
			this.states.direction = 0;
		} else {
			this.states.direction = -1;
		}
	}

	updateTopPrevious() {
		this.coords.top = window.scrollY || document.documentElement.scrollTop || 0;
		this.coords.bottom = this.coords.top + this.coords.viewportHeight;
		this.states.percent = this.coords.top / (this.coords.height - this.coords.viewportHeight);
	}

	updateJumpTo() {
		this.ease.from = this.coords.top;
		this.ease.frame = 1;
	}

	updateEaseTo() {
		this.ease.from = this.coords.eased;
		this.ease.frame = this.ease.duration;
	}

	updateChildren() {
		for (const child of this.children) {
			if (child.isSticky) {
				this.updateStickyChild(child);
			} else {
				let visibility = null;
				let translate = 0;

				if (this.coords.eased + this.coords.viewportHeight <= child.offset) {
					visibility = 'hidden';
					translate = child.offset - this.coords.viewportHeight;
				} else if (this.coords.eased >= child.offset + child.height) {
					visibility = 'hidden';
					translate = child.offset + child.height;
				} else {
					visibility = 'visible';
					translate = this.coords.eased;
				}

				if (visibility !== child.visibility || translate !== child.translate) {
					child.visibility = visibility;
					child.translate = translate;
					child.update = true;
				}
			}
		}
	}

	updateStickyChild(child) {
		if (child.isSticky) {
			const visibility = 'visible';
			const translate = 0; // child.space; // TODO: this needs to be figured out

			if (visibility !== child.visibility || translate !== child.translate) {
				child.visibility = visibility;
				child.translate = translate;
				child.update = true;
			}
		}
	}

	updateStickies() {
		for (const sticky of this.stickies) {
			const offset = this.coords.sticky.top + this.coords.sticky.offset;
			const height = sticky.height + this.coords.sticky.top * 2;

			if (height > this.coords.viewportHeight) {
				const difference = height - this.coords.viewportHeight;
				sticky.adjustment += this.states.chunk;
				if (sticky.adjustment < 0) sticky.adjustment = 0;
				if (sticky.adjustment > difference) sticky.adjustment = difference;
			} else {
				sticky.adjustment = 0;
			}

			let translate = 0;

			if (this.coords.eased < sticky.offset - offset + sticky.adjustment) {
				translate = 0;
			} else if (this.coords.eased > sticky.offset - offset + sticky.adjustment + sticky.space - sticky.height) {
				translate = sticky.space - sticky.height;
			} else {
				translate = this.coords.eased - sticky.offset + offset - sticky.adjustment;
			}

			if (translate !== sticky.translate) {
				sticky.translate = translate;
				sticky.update = true;
			}
		}
	}

	updateEased() {
		const eased = Math.round(this.ease.from + easing[this.ease.function](this.ease.duration - this.ease.frame, 0, this.coords.top - this.ease.from, this.ease.duration));
		this.states.chunk = eased - this.coords.eased;
		if (eased !== this.coords.eased) {
			this.coords.eased = eased;
			this.coords.easedBottom = this.coords.eased + this.coords.viewportHeight;
			this.updateChildren();
			this.updateStickies();
			return true;
		}
	}

	// RAF update and render methods

	update() {
		const windowY = window.scrollY || document.documentElement.scrollTop || 0;
		if (!this.flags.isLocked && windowY !== this.coords.top) {
			this.updateDirection();
			this.updateTopPrevious();

			if (this.flags.isSmoothScrolling) {
				if (this.flags.isJumpMode) {
					this.flags.isJumpMode = false;
					this.updateJumpTo();
				} else {
					this.updateEaseTo();
				}
			}
		}

		if (this.flags.isSmoothScrolling && this.ease.frame > 0) {
			--this.ease.frame;
			return this.updateEased();
		}
	}

	render() {
		if (!this.flags.isLocked && this.flags.isSmoothScrolling && this.flags.isRendering) {
			for (const child of this.children) {
				if (child.update) {
					child.update = false;
					child.el.style.visibility = child.visibility;
					child.el.style.transform = `matrix(1, 0, 0, 1, 0, -${child.translate})`;
				}
			}
			for (const sticky of this.stickies) {
				if (sticky.update) {
					sticky.update = false;
					sticky.el.style.transform = `matrix(1, 0, 0, 1, 0, ${sticky.translate})`;
					sticky.el.dataset.adjustment = sticky.adjustment;
				}
			}
		}
	}

	// reset scrolling elements

	refresh(gather) {
		if (gather) {
			this.gatherChildren();
			this.gatherStickies();
		}

		this.adjust();
	}

	// respond to resize

	resize() {
		this.resizeUpdate();
		this.resizeRender();
	}

	resizeUpdate() {
		this.measureViewport();
		this.measureChildCoordinates();
	}

	resizeRender() {
		this.adjustRender();
	}

	// if dom changes, update measurements for all objects

	adjust() {
		if (this.flags.isSmoothScrolling) {
			this.measureChildCoordinates();
			this.adjustRender();
		}
	}

	measureChildCoordinates() {
		if (this.flags.isSmoothScrolling) {
			this.measureChildren();
			this.measureStickies();
		}
	}

	adjustRender() {
		if (this.flags.isSmoothScrolling) {
			this.updateChildren();
			this.updateStickies();
			this.updateHeight();
		}
	}

	// enable / disable

	enableSmoothScrolling(force = false) {
		if (!this.flags.isSmoothScrolling || force) {
			const windowY = window.scrollY || document.documentElement.scrollTop || 0;
			this.scrollTo(windowY, true);

			this.flags.isSmoothScrolling = true;
			document.body.classList.add('is-smooth-scrolling');
			document.body.classList.remove('is-default-scrolling');

			//

			this.coords.top = windowY;
			this.coords.bottom = this.coords.top + this.coords.viewportHeight;
			this.coords.eased = windowY;
			this.coords.easedBottom = this.coords.eased + this.coords.viewportHeight;
			this.ease.from = windowY;

			this.updateChildren();
			this.updateStickies();
			this.render();

			this.setStyles();
			this.resize();
		}
	}

	disableSmoothScrolling(force = false) {
		if (this.flags.isSmoothScrolling || force) {
			this.flags.isSmoothScrolling = false;
			document.body.classList.add('is-default-scrolling');
			document.body.classList.remove('is-smooth-scrolling');
			this.removeStyles();
			this.resize();
		}
	}

	// prepare container for scrolling children

	setStyles() {
		this.container.style.width = '100%';
		this.container.style.position = 'fixed';
		this.container.style.left = 0;
		this.container.style.top = 0;
	}

	// reset container to original state

	removeStyles() {
		HeightController.removeStyles();

		this.container.style.height = '';
		this.container.style.width = '';
		this.container.style.position = '';
		this.container.style.left = '';
		this.container.style.top = '';

		for (const child of this.children) {
			child.el.style.visibility = '';
			child.el.style.transform = '';
		}

		for (const sticky of this.stickies) {
			sticky.el.style.transform = '';
		}
	}

	// commands to allow children to stick or unstick at will
	// this will not work for children marked as sticky
	// this will also not provide automatic stick / unstick behavior to normal children

	stick(el) {
		let curChild = null;
		for (let i = 0; i < this.children.length; i++) {
			const child = this.children[i];
			if (child.el === el) {
				curChild = child;
				break;
			}
		}

		if (curChild) {
			curChild.isSticky = true;
			this.updateChildren();
		}
	}

	unstick(el) {
		let curChild = null;
		for (let i = 0; i < this.children.length; i++) {
			const child = this.children[i];
			if (child.el === el) {
				curChild = child;
				break;
			}
		}

		if (curChild) {
			curChild.isSticky = false;
			this.updateChildren();
		}
	}

	//

	restorePosition() {
		if (this.states.percent && this.flags.isRendering) {
			this.scrollTo((this.coords.height - this.coords.viewportHeight) * this.states.percent, true);
		}
	}

	// <==]--------------------------->
	// getters

	get enabled() {
		return this.flags.isSmoothScrolling;
	}

	get isSmoothScrolling() {
		return this.flags.isSmoothScrolling;
	}

	// coords

	get top() {
		return this.coords.top;
	}

	get bottom() {
		return this.coords.bottom;
	}

	get viewportHeight() {
		return this.coords.viewportHeight;
	}

	get eased() {
		return this.coords.eased;
	}

	get easedBottom() {
		return this.coords.easedBottom;
	}

	// states

	get chunk() {
		return this.states.chunk;
	}

	get direction() {
		return this.states.direction;
	}

	get percent() {
		return this.states.percent;
	}

	// determines whether a scroll is in progress

	get scrolling() {
		return !this.flags.isSmoothScrolling || (this.flags.isRendering && this.states.chunk !== 0);
	}

	// <==]--------------------------->
	// getters & setters

	// determines whether to render scroll position or not

	get rendering() {
		return this.flags.isRendering;
	}

	set rendering(value) {
		this.flags.isRendering = value;
	}

	// determines whether to jump or smooth scroll to a given position

	get jump() {
		return this.flags.isJumpMode;
	}

	set jump(value) {
		this.flags.isJumpMode = value;
	}

	// <==]--------------------------->
	// setters

	// determines whether a scroll is in progress

	set stickyTop(top = 0) {
		this.coords.sticky.top = top;
	}

	set stickyOffset(offset = 0) {
		this.coords.sticky.offset = offset;
	}

}

export default new ScrollController();
